import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import {
  BlockQuote,
  Bold,
  ClassicEditor,
  EditorConfig,
  Heading,
  Italic,
  Link,
  List,
  Table,
  TableToolbar,
} from "ckeditor5";

import { LocaleService } from "../../services/locale.service";
import type { TranslationLanguage } from "../../types/translation.types";
import { TranslatableFieldBase } from "./translatable-field.base";
import { TranslatableShellComponent } from "./translatable-shell.component";
import { atLeastOneLocaleExpression, isHtmlEmpty } from "./translatable.utils";

export interface CKEditorTranslatableFieldProps {
  fieldBaseName: string;
  sourceLanguage?: TranslationLanguage;
  showTranslateButton?: boolean;
  editorConfig?: EditorConfig;
  label?: string;
  editorClass?: string;
  /**
   * Require at least one locale to be filled. Replaces the standard
   * `required` prop, which would only check the NL slot via Formly's
   * built-in `Validators.required`.
   */
  requireOneLocale?: boolean;
}

const BASE_EDITOR_CONFIG: EditorConfig = {
  licenseKey: "GPL",
  plugins: [Heading, Link, Bold, Table, TableToolbar, Italic, List, BlockQuote],
  language: "nl",
  toolbar: [
    "heading",
    "|",
    "inserttable",
    "|",
    "bold",
    "italic",
    "link",
    "bulletedList",
    "numberedList",
    "blockQuote",
  ],
  heading: {
    options: [
      {
        model: "paragraph" as const,
        title: "Paragraph",
        class: "ck-heading_paragraph",
      },
      {
        model: "heading1" as const,
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2" as const,
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3" as const,
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
      {
        model: "heading4" as const,
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4",
      },
      {
        model: "heading5" as const,
        view: "h5",
        title: "Heading 5",
        class: "ck-heading_heading5",
      },
      {
        model: "heading6" as const,
        view: "h6",
        title: "Heading 6",
        class: "ck-heading_heading6",
      },
    ],
  },
};

@Component({
  selector: "app-formly-ckeditor-translatable",
  standalone: true,
  imports: [FormsModule, CKEditorModule, TranslatableShellComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-translatable-shell [field]="this">
      <ng-template let-lang>
        <ckeditor
          [editor]="Editor"
          [class]="props['editorClass']"
          [config]="editorConfig"
          [ngModel]="getFieldValue(lang)"
          (ngModelChange)="setFieldValue(lang, $event)"
          (blur)="markTouched()"
        ></ckeditor>
      </ng-template>
    </app-translatable-shell>
  `,
})
export class CKEditorTranslatableFieldComponent extends TranslatableFieldBase {
  private readonly localeService = inject(LocaleService);

  readonly Editor = ClassicEditor;
  editorConfig: EditorConfig = BASE_EDITOR_CONFIG;

  override defaultOptions = {
    props: {
      fieldBaseName: "",
      sourceLanguage: "nl" as TranslationLanguage,
      showTranslateButton: true,
      editorClass: "editor_xl",
    },
    validators: {
      atLeastOneLocale: {
        expression: atLeastOneLocaleExpression,
        message: "This field is required",
      },
    },
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.editorConfig = {
      ...BASE_EDITOR_CONFIG,
      language: this.localeService.getCurrentLanguage(),
      ...(this.props["editorConfig"] ?? {}),
    };
  }

  protected override normaliseValue(value: string): string | null {
    return isHtmlEmpty(value) ? null : value;
  }
}
